import { useState, useEffect, useCallback } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sidebar } from "@/components/Sidebar";
import { CommandPalette, ShortcutsModal } from "@/components/CommandPalette";
import { Loader2 } from "lucide-react";

const NAV_PATHS = ["/", "/projects", "/repository", "/graph", "/pipelines", "/review", "/integrations", "/observability"];

export const AppShell = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showCmdPalette, setShowCmdPalette] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);

  const handleAction = useCallback((action) => {
    if (action === "new-project") navigate("/projects");
    if (action === "shortcuts") setShowShortcuts(true);
  }, [navigate]);

  useEffect(() => {
    const handler = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && e.key === "k") { e.preventDefault(); setShowCmdPalette(p => !p); }
      if (ctrl && e.key === "/") { e.preventDefault(); setShowShortcuts(p => !p); }
      if (ctrl && e.key >= "1" && e.key <= "8") {
        e.preventDefault();
        const idx = parseInt(e.key) - 1;
        if (NAV_PATHS[idx]) navigate(NAV_PATHS[idx]);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-cobalt" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-shell">
      <Sidebar />
      <main className="flex-1 overflow-y-auto bg-background">
        <Outlet />
      </main>
      {showCmdPalette && <CommandPalette onClose={() => setShowCmdPalette(false)} onAction={handleAction} />}
      {showShortcuts && <ShortcutsModal onClose={() => setShowShortcuts(false)} />}
    </div>
  );
};
